/**
 * NOTE on redux: `customerPreferences` and `couponRangeList` are new modules
 * built specifically for this screen (and shared with SignUp/PreferencesForm
 * for the range list) — neither existed anywhere before. Endpoints are best
 * guesses following existing naming conventions; verify against the real
 * backend. See modules/customerPreferences and modules/couponRangeList.
 *
 * `FormFields` removed earlier: replaced with raw snake_case strings
 * (coupon_search_range, categories) matching customerPreferences.preferences.
 *
 * TEMPORARY: `formMessages` (from SignUp/PreferencesForm/messages) removed
 * since that file doesn't exist yet either. The usages are stubbed with
 * inline { id, defaultMessage } objects (getNormalizedEntity calls
 * intl.formatMessage on these, which needs an `id`, not a bare string).
 * Search for "STUB:" below to find every spot.
 *
 * NOTE on styling: react-css-modules only resolves `styleName` on elements
 * in the tree this component returns directly. Elements created inside a
 * render prop (the <Controller render={...}> callbacks) are never processed,
 * so those use `className={styles[...]}` instead.
 */
import React, { useCallback, useEffect, useState } from 'react';

import CSSModule from 'react-css-modules';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Spinner } from 'components';
import { actions as categoriesActions, duck } from 'coupon-common/src/modules/businessCategories';
import { actions as couponRangeListActions } from 'coupon-common/src/modules/couponRangeList';
import { actions as preferencesActions } from 'coupon-common/src/modules/customerPreferences';
import {
  dispatchAsAsync,
  getFormattedLoadingError,
  getNormalizedEntity,
  getObjectsDiff,
} from 'coupon-common/src/utils';

import messages from './messages';
import styles from './Preferences.module.scss';

const categoriesKey = duck.businessCategoriesEntity.key;

// Field names on customerPreferences.preferences (see modules/customerPreferences/duck.js).
const FIELD_COUPON_SEARCH_RANGE = 'coupon_search_range';
const FIELD_CATEGORIES = 'categories';

// API returns categories as full objects but only accepts pks on write.
const toCategoryIds = prefs => ({
  ...prefs,
  categories: ((prefs && prefs.categories) || []).map(c => (c && typeof c === 'object' ? c.id : c)),
});

const Preferences = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const businessCategories = useSelector(state => state.businessCategories);
  const couponRangeList = useSelector(state => state.couponRangeList);
  const customerPreferences = useSelector(state => state.customerPreferences);

  const [toast, setToast] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isDirty },
    } = useForm({ defaultValues: customerPreferences.preferences });

  const getFormData = useCallback(() => {
    dispatch(categoriesActions.getBusinessCategories());
    dispatch(couponRangeListActions.getCouponRangeList());
  }, [dispatch]);

  const getPreferencesData = useCallback(() => {
    dispatch(preferencesActions.getPreferences());
  }, [dispatch]);

  useEffect(() => {
    getFormData();
    getPreferencesData();
  }, [getFormData, getPreferencesData]);

  const { getPreferences } = customerPreferences;

  // useForm only reads defaultValues on first render (before the fetch
  // completes), so push the saved preferences into the form once they land
  // and again after each successful save.
  useEffect(() => {
    if (getPreferences.fetched) {
      reset(toCategoryIds(customerPreferences.preferences));
    }
  }, [getPreferences.fetched, customerPreferences.preferences, reset]);

  const selectedCategories = watch(FIELD_CATEGORIES) || [];

  // STUB: was formMessages.entityCategories
  const categoriesNormalized = getNormalizedEntity(businessCategories, categoriesKey, intl, {
    id: 'cb.screens.Preferences.entityCategories',
    defaultMessage: 'business categories',
  });

  const showToast = text => {
    setToast(text);
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async formValues => {
        const updatedValues = getObjectsDiff(toCategoryIds(customerPreferences.preferences), formValues);

    if (Object.keys(updatedValues).length > 0) {
      const errors = await dispatchAsAsync(preferencesActions.updatePreferences(formValues), dispatch);

      showToast(
        intl.formatMessage(errors ? messages.couldNotUpdatePreferencesError : messages.preferencesSaved),
      );
    }
  };

  // STUB: was formMessages.couponRangeListKey
  const formattedCouponRangeError = getFormattedLoadingError(intl, couponRangeList.error, {
    id: 'cb.screens.Preferences.couponRangeListKey',
    defaultMessage: 'coupon range list',
  });

  if (getPreferences.error) {
    return (
      <div styleName="page">
        <p styleName="state-message">Something went wrong loading your preferences.</p>
      </div>
    );
  }

  if (getPreferences.fetching && !getPreferences.fetched) {
    return (
      <div styleName="page" style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner show size="lg" />
      </div>
    );
  }

  return (
    <div styleName="page">
      <div styleName="card">
        <Link to="/my-account" styleName="back-link">
          <FormattedMessage {...messages.backToAccount} />
        </Link>

        <h1 styleName="title">
          <FormattedMessage {...messages.title} />
        </h1>
        <p styleName="subtitle">
          <FormattedMessage {...messages.subtitle} />
        </p>

        <section styleName="section">
          <label styleName="section-title" htmlFor="prefs-range">
            <FormattedMessage {...messages.selectDistance} />
          </label>
          
          <Controller
            control={control}
            name={FIELD_COUPON_SEARCH_RANGE}
            render={({ field: { onChange, value } }) => (
              <select
                id="prefs-range"
                className={styles.select}
                value={value ?? ''}
                onChange={e => onChange(Number(e.target.value))}
                disabled={!!couponRangeList.fetching || !!formattedCouponRangeError}
              >
                <option value="" disabled>
                  Select a range
                </option>
                {couponRangeList.options.map(opt => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
            )}
          />
          {formattedCouponRangeError && <p styleName="feedback">{formattedCouponRangeError}</p>}
        </section>

        <section styleName="section">
          <div styleName="section-head">
            <h2 styleName="section-title">
              <FormattedMessage {...messages.categoriesTitle} />
            </h2>
            <span styleName="count">{selectedCategories.length} selected</span>
          </div>
          <p styleName="hint">
            <FormattedMessage {...messages.categoriesHint} />
          </p>
          <Controller
            control={control}
            name={FIELD_CATEGORIES}
            render={({ field: { onChange, value } }) => (
              <div className={styles['tag-cloud']}>
                {categoriesNormalized.isFetching && <Spinner show size="sm" />}
                {(categoriesNormalized.entityList || []).map(cat => {
                  const selected = (value || []).includes(cat.id);
                  return (
                    <button
                      type="button"
                      key={cat.id}
                      aria-pressed={selected}
                      className={selected ? `${styles.tag} ${styles['tag-selected']}` : styles.tag}
                      onClick={() =>
                        onChange(selected ? value.filter(id => id !== cat.id) : [...(value || []), cat.id])
                      }
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            )}
          />
        </section>

        <div styleName="submit-row">
          <button
            type="button"
            styleName="save-btn"
            onClick={handleSubmit(handleSave)}
            disabled={isSubmitting || !isDirty}
          >
            {isSubmitting ? <Spinner show size="sm" /> : intl.formatMessage(messages.saveBtn)}
          </button>
        </div>
      </div>

      {toast && <div styleName="toast">{toast}</div>}
    </div>
  );
};

export default CSSModule(Preferences, styles, { allowMultiple: true });